import {RouterModule, Routes} from '@angular/router';
import {NgModule} from '@angular/core';

import {LoginComponent} from "./feature/login/login.component";
import {HomeComponent} from "./feature/home/home.component";
import {DashboardComponent} from "./feature/dashboard/dashboard.component";
import {AppLayoutComponent} from "./layout/app.layout.component";
import {roleRedirectGuard} from "./core/guards/role-redirect.guard";
import {comingSoonGuard} from "./core/guards/coming-soon.guard";

import {roleGuard} from "./core/service/auth/auth.guard";

import {RoleEnum} from "./core/modules/enums/roleEnum";
import {HomeGuard} from "./core/service/auth/home-guard.service";
import {SUPER_ADMIN_PERMISSION} from "./core/modules/admin-permissions";
import {ADMIN_INVITATIONS_ACCESS} from "./core/modules/admin-invitations/admin-invitation.access";
import {INVOICES_ACCESS} from "./core/modules/invoices/invoice.access";
import {BANK_OPS_ACCESS} from "./core/modules/bank-operations/bank-ops.access";
import {AI_ASSISTANT_ACCESS, AI_COSTS_ACCESS, AI_MODELS_ACCESS} from "./core/modules/ai-assistant/ai-admin.access";
import {WATHQ_COSTS_ACCESS} from "./core/modules/wathq/wathq-costs.models";
import {BULK_IMPORT_JOBS_ACCESS, BULK_IMPORT_SETTINGS_ACCESS} from "./core/modules/product-imports/bulk-import.access";
import {PAYMENT_SETTINGS_ACCESS} from "./core/modules/payment-settings/payment-settings.models";
import {FeatureAudience} from "./feature/subscription-plans/models/subscription-catalog.model";

export const routes: Routes = [
  {
    path: '', component: AppLayoutComponent, canActivate: [HomeGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        canActivate: [roleRedirectGuard],
        component: AppLayoutComponent
      },
      {
        path: 'home',
        component: HomeComponent,
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin]}
      },
      {
        path: 'profile',
        loadComponent: () => import("./feature/profile/profile.component").then(m => m.ProfileComponent),
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin]}
      },
      {
        path: 'sales',
        children: [
          {
            path: 'managers', loadComponent: () => import("./feature/sales/sales-managers/sales-managers.component").then(m => m.SalesManagersComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.Admin]}
          },
          {
            path: 'salesmen', loadComponent: () => import("./feature/sales/salesmen/salesmen.component").then(m => m.SalesmenComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.SalesManager, RoleEnum.Admin]}
          },
          {
            path: 'teams', loadComponent: () => import("./feature/sales/sales-teams/sales-teams.component").then(m => m.SalesTeamsComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.SalesManager, RoleEnum.Admin]}
          },
          {
            // Per-team commission rules and tier ladder, resolved against the global config.
            path: 'teams/:teamId/config', loadComponent: () => import("./feature/sales/team-config/team-config.component").then(m => m.TeamConfigComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.SalesManager, RoleEnum.Admin]}
          },
          {
            path: 'customer-services', loadComponent: () => import("./feature/sales/customer-services/customer-services.component").then(m => m.CustomerServicesComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.SalesManager, RoleEnum.Admin]}
          },
          {
            // Also the Care dashboard: the API narrows the returned KPIs to the caller's role.
            path: 'kpis', loadComponent: () => import("./feature/sales/sales-kpis/sales-kpis.component").then(m => m.SalesKpisComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.SalesManager, RoleEnum.Admin, RoleEnum.CustomerService]}
          },
          {
            path: 'kpi-targets', loadComponent: () => import("./feature/sales/sales-kpi-targets/sales-kpi-targets.component").then(m => m.SalesKpiTargetsComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.SalesManager, RoleEnum.Admin]}
          },
          {
            path: 'commission-config', loadComponent: () => import("./feature/sales/commission-config/commission-config.component").then(m => m.CommissionConfigComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.SalesManager, RoleEnum.Admin]}
          },
          {
            path: 'star-rating', loadComponent: () => import("./feature/sales/star-rating/star-rating.component").then(m => m.StarRatingComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.SalesManager, RoleEnum.Admin]}
          },
          {
            path: 'commission', loadComponent: () => import("./feature/sales/commission/commission.component").then(m => m.CommissionComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.SalesManager, RoleEnum.Admin]}
          },
          {
            path: 'my-profile', loadComponent: () => import("./feature/sales/my-profile/my-profile.component").then(m => m.MyProfileComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.Salesman]}
          },
          {
            path: 'my-commission', loadComponent: () => import("./feature/sales/my-commission/my-commission.component").then(m => m.MyCommissionComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.Salesman]}
          },
          {
            path: 'my-referred-customers', loadComponent: () => import("./feature/sales/my-referred-customers/my-referred-customers.component").then(m => m.MyReferredCustomersComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.Salesman]}
          },
          {
            path: 'my-reports', loadComponent: () => import("./feature/sales/sales-reports/my-sales-reports/my-sales-reports.component").then(m => m.MySalesReportsComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.Salesman]}
          },
          {
            path: 'invitation-links', loadComponent: () => import("./feature/sales/invitation-links/invitation-links.component").then(m => m.InvitationLinksComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.Salesman]}
          },
          {
            path: 'reports', loadComponent: () => import("./feature/sales/sales-reports/sales-reports.component").then(m => m.SalesReportsComponent),
            canMatch: [roleGuard], canActivate: [roleGuard],
            data: {roles: [RoleEnum.SalesManager, RoleEnum.Admin]}
          },
        ]
      },
      {
        path: 'users', loadComponent: () => import("./feature/user-managment/user-management.component").then(m => m.UserManagementComponent),

        canMatch: [roleGuard],      // Applies to all children
        canActivate: [roleGuard],   // Applies to all children
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'users'}, // Base role requirement,

      },
      {
        path: 'reports', loadComponent: () => import("./feature/reports/reports.component").then(m => m.ReportsComponent),

        canMatch: [roleGuard],      // Applies to all children
        canActivate: [roleGuard],   // Applies to all children
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'reports'}
      },
      {
        path: 'ai-cost-monitoring', loadComponent: () => import("./feature/ai-cost-monitoring/ai-cost-monitoring.component").then(m => m.AiCostMonitoringComponent),

        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {...AI_COSTS_ACCESS}
      },
      {
        path: 'wathq-costs', loadComponent: () => import("./feature/wathq-costs/wathq-costs.component").then(m => m.WathqCostsComponent),
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {...WATHQ_COSTS_ACCESS}
      },

      {
        path: 'suppliers'
        , children: [
          {path: '', loadComponent: () => import("./feature/suppliers/suppliers.component").then(m => m.SuppliersComponent)},
          {path: 'leaderboard', loadComponent: () => import("./feature/suppliers/supplier-leaderboard/supplier-leaderboard.component").then(m => m.SupplierLeaderboardComponent)},
          // Read-only registry lookup. Declares the exact claim its endpoint checks,
          // which is stricter than the parent's suppliers group.
          {
            path: 'registration-lookup',
            loadComponent: () => import("./feature/wathq/registration-lookup/registration-lookup.component").then(m => m.RegistrationLookupComponent),
            canActivate: [roleGuard],
            data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permission: 'suppliers.view'}
          },
          {path: 'details/:supplierId', loadComponent: () => import("./feature/suppliers/supplier-details/supplier-details.component").then(m => m.SupplierDetailsComponent)}
        ],

        canMatch: [roleGuard],      // Applies to all children
        canActivate: [roleGuard],   // Applies to all children
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'suppliers'}
      },
      {
        path: 'restaurants'
        , children: [
          {path: '', loadComponent: () => import("./feature/restaurants/restaurants.component").then(m => m.RestaurantsComponent)},
          {path: 'details/:restaurantId', loadComponent: () => import("./feature/restaurants/restaurant-details/restaurant-details.component").then(m => m.RestaurantDetailsComponent)}
        ],

        canMatch: [roleGuard],      // Applies to all children
        canActivate: [roleGuard],   // Applies to all children
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin]}
      },
      {
        path: 'buyers',
        children: [
          {path: '', loadComponent: () => import("./feature/buyers/buyers.component").then(m => m.BuyersComponent)},
          {path: 'details/:buyerId', loadComponent: () => import("./feature/buyers/buyer-details/buyer-details.component").then(m => m.BuyerDetailsComponent)},
          // An owner's profile by URL alone (no navigation state needed, so refresh
          // and shared links work). Also a distinct route from details/:buyerId, so
          // opening a branch's owner from its profile builds a fresh component
          // instead of reusing the branch's with stale state.
          {path: 'owners/:buyerId', loadComponent: () => import("./feature/buyers/buyer-details/buyer-details.component").then(m => m.BuyerDetailsComponent), data: {buyerType: 'BusinessOwner'}},
          // Same, for a hotel opened from an in-hotel restaurant's profile.
          {path: 'hotels/:buyerId', loadComponent: () => import("./feature/buyers/buyer-details/buyer-details.component").then(m => m.BuyerDetailsComponent), data: {buyerType: 'Hotel'}}
        ],
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'buyers'}
      },
      // Bulk import jobs and limits. Kept under the /products URL but declared before the products
      // parent, whose products group would refuse a SubAdmin holding only the bulk import permissions.
      {
        path: 'products/imports',
        children: [
          {
            path: '',
            loadComponent: () => import("./feature/product-imports/product-imports.component").then(m => m.ProductImportsComponent),
            canActivate: [roleGuard],
            data: {...BULK_IMPORT_JOBS_ACCESS}
          },
          {
            path: 'details/:jobId',
            loadComponent: () => import("./feature/product-imports/product-import-details/product-import-details.component").then(m => m.ProductImportDetailsComponent),
            canActivate: [roleGuard],
            data: {...BULK_IMPORT_JOBS_ACCESS}
          },
          {
            path: 'settings',
            loadComponent: () => import("./feature/product-imports/import-limits/import-limits.component").then(m => m.ImportLimitsComponent),
            canActivate: [roleGuard],
            data: {...BULK_IMPORT_SETTINGS_ACCESS}
          }
        ]
      },
      {
        path: 'products',
        children: [
          {path: '', loadComponent: () => import("./feature/products/products.component").then(m => m.ProductsComponent)},
          {path: 'details/:productId', loadComponent: () => import("./feature/products/product-details/product-details.component").then(m => m.ProductDetailsComponent)},
          {path: 'hs-review', loadComponent: () => import("./feature/hs-review/hs-review.component").then(m => m.HsReviewComponent)},
        ],

        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'products'}
      },
      {
        path: 'certificates',
        children: [
          {path: '', loadComponent: () => import("./feature/certificates/certificates.component").then(m => m.CertificatesComponent)},
          {path: 'details/:certificateId', loadComponent: () => import("./feature/certificates/certificate-details/certificate-details.component").then(m => m.CertificateDetailsComponent)}
        ],
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'certificates'}
      },
      {
        path: 'certificate-settings', loadComponent: () => import("./feature/certificate-settings/certificate-settings.component").then(m => m.CertificateSettingsComponent),
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'certificates'}
      },
      {
        path: 'dashboard', component: DashboardComponent,
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'dashboard'}
      },
      {
        path: 'command-center',
        children: [
          {path: '', loadComponent: () => import("./feature/command-center/command-center.component").then(m => m.CommandCenterComponent)},
          {path: 'stuck-orders', loadComponent: () => import("./feature/command-center/stuck-orders/stuck-orders.component").then(m => m.StuckOrdersComponent)},
          {path: 'overdue-bills', loadComponent: () => import("./feature/command-center/overdue-bills/overdue-bills.component").then(m => m.OverdueBillsComponent)},
          {path: 'at-risk-buyers', loadComponent: () => import("./feature/command-center/at-risk-buyers/at-risk-buyers.component").then(m => m.AtRiskBuyersComponent)},
          {path: 'expiring-subscriptions', loadComponent: () => import("./feature/command-center/expiring-subscriptions/expiring-subscriptions.component").then(m => m.ExpiringSubscriptionsComponent)},
          {path: 'failed-renewals', loadComponent: () => import("./feature/command-center/failed-renewals/failed-renewals.component").then(m => m.FailedRenewalsComponent)},
        ],
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'dashboard'}
      },
      // Platform invoices and their Odoo sync. Both spread the same constant as their sidenav
      // entries, so what a SubAdmin sees is exactly what they can open.
      {
        path: 'invoices',
        loadComponent: () => import("./feature/invoices/invoices.component").then(m => m.InvoicesComponent),
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {...INVOICES_ACCESS}
      },
      {
        path: 'odoo',
        loadComponent: () => import("./feature/odoo/odoo.component").then(m => m.OdooComponent),
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {...INVOICES_ACCESS}
      },
      // Platform commission and order-deposit settings. View opens them; saving also needs Manage,
      // which each screen checks for its Save button.
      {
        path: 'payment-settings',
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {...PAYMENT_SETTINGS_ACCESS},
        children: [
          {path: '', pathMatch: 'full', redirectTo: 'commission'},
          {path: 'commission', loadComponent: () => import("./feature/payment-settings/commission-settings/commission-settings.component").then(m => m.CommissionSettingsComponent)},
          {path: 'order-deposit', loadComponent: () => import("./feature/payment-settings/deposit-settings/deposit-settings.component").then(m => m.DepositSettingsComponent)},
        ]
      },
      // Bank operations (/api/admin/banking). The shell carries the money-out freeze banner over
      // every screen; the route and the sidenav spread the same constant.
      {
        path: 'bank-operations',
        loadComponent: () => import("./feature/bank-operations/bank-operations-shell.component").then(m => m.BankOperationsShellComponent),
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {...BANK_OPS_ACCESS},
        children: [
          {path: '', redirectTo: 'overview', pathMatch: 'full'},
          {path: 'overview', loadComponent: () => import("./feature/bank-operations/overview/bank-ops-overview.component").then(m => m.BankOpsOverviewComponent)},
          {path: 'platform-accounts', loadComponent: () => import("./feature/bank-operations/platform-accounts/bank-ops-platform-accounts.component").then(m => m.BankOpsPlatformAccountsComponent)},
          {path: 'exceptions', loadComponent: () => import("./feature/bank-operations/exceptions/bank-ops-exceptions.component").then(m => m.BankOpsExceptionsComponent)},
          {path: 'parked-messages', loadComponent: () => import("./feature/bank-operations/parked-messages/bank-ops-parked-messages.component").then(m => m.BankOpsParkedMessagesComponent)},
          {path: 'freeze', loadComponent: () => import("./feature/bank-operations/freeze/bank-ops-freeze.component").then(m => m.BankOpsFreezeComponent)},
          {path: 'reconciliation', loadComponent: () => import("./feature/bank-operations/reconciliation/bank-ops-reconciliation.component").then(m => m.BankOpsReconciliationComponent)},
          {path: 'sweep', loadComponent: () => import("./feature/bank-operations/sweep/bank-ops-sweep.component").then(m => m.BankOpsSweepComponent)},
          {path: 'unverified-suppliers', loadComponent: () => import("./feature/bank-operations/unverified-suppliers/bank-ops-unverified-suppliers.component").then(m => m.BankOpsUnverifiedSuppliersComponent)},
          {path: 'bank-credits', loadComponent: () => import("./feature/bank-operations/bank-credits/bank-ops-bank-credits.component").then(m => m.BankOpsBankCreditsComponent)},
          {path: 'diagnostics', loadComponent: () => import("./feature/bank-operations/diagnostics/bank-ops-diagnostics.component").then(m => m.BankOpsDiagnosticsComponent)},
        ]
      },
      {
        path: 'bill-management',
        loadComponent: () => import("./feature/bill-management/bill-management.component").then(m => m.BillManagementComponent),
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'buyers'}
      },
      {
        path: 'audit-logs', loadComponent: () => import("./feature/audit-logs/audit-logs.component").then(m => m.AuditLogsComponent),
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'auditlog'}
      },
      {
        path: "settlement", loadComponent: () => import("./feature/settelment/settlements/settlements.component").then(m => m.SettlementsComponent),
        canMatch: [comingSoonGuard],
        canActivate: [comingSoonGuard],
      },
      {
        path: "orders",
        children: [
          {path: '', loadComponent: () => import("./feature/orders/orders.component").then(m => m.OrdersComponent)},
          {path: 'details/:orderId', loadComponent: () => import("./feature/orders/order-details/order-details.component").then(m => m.OrderDetailsComponent)}
        ],
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'orders'}
      },
      {
        path: "request-order",
        children: [
          {path: '', loadComponent: () => import("./feature/request-orders/request-orders/request-orders.component").then(m => m.RequestOrdersComponent)},
          {path: 'details/:requestOrderId', loadComponent: () => import("./feature/request-orders/request-order-details/request-order-details.component").then(m => m.RequestOrderDetailsComponent)}
        ],

        canMatch: [roleGuard],      // Applies to all children
        canActivate: [roleGuard],   // Applies to all children
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'requestorders'}
      },
      {
        path: 'ads',
        children: [
          {path: '', loadComponent: () => import("./feature/ads/ads.component").then(m => m.AdsComponent)},
          {path: 'details/:id', loadComponent: () => import("./feature/ads/ad-details/ad-details.component").then(m => m.AdDetailsComponent)}
        ],
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'ads'}
      },
      {
        path: 'returns',
        children: [
          {path: '', loadComponent: () => import("./feature/returns/returns.component").then(m => m.ReturnsComponent)},
          {path: 'details/:id', loadComponent: () => import("./feature/returns/return-details/return-details.component").then(m => m.ReturnDetailsComponent)}
        ],
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'returns'}
      },
      {
        path: 'complaints',
        children: [
          {path: '', loadComponent: () => import("./feature/complaints/complaints.component").then(m => m.ComplaintsComponent)},
          {path: 'details/:type/:id', loadComponent: () => import("./feature/complaints/complaint-details/complaint-details.component").then(m => m.ComplaintDetailsComponent)}
        ],
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'complaints'}
      },
      {
        path: 'reviews',
        loadComponent: () => import("./feature/reviews/reviews.component").then(m => m.ReviewsComponent),
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'reviews'}
      },
      {
        path: 'categories',
        loadComponent: () => import("./feature/commons/categories/categories.component").then(m => m.CategoriesComponent),
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin]}
      },
      {
        path: 'ai-assistant', loadComponent: () => import("./feature/ai-assistant/ai-assistant.component").then(m => m.AiAssistantComponent),
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        // Each tab is gated inside the page; the page itself opens for any one of them.
        data: {...AI_ASSISTANT_ACCESS}
      },
      // The document list moved into the AI Assistant page's Knowledge base tab;
      // this keeps existing links and bookmarks working.
      {path: 'upload-document', redirectTo: 'ai-assistant', pathMatch: 'full'},
      {
        path: 'upload-pdf', loadComponent: () => import("./feature/upload-pdf/upload-pdf.component").then(m => m.UploadPdfComponent),
        canMatch: [roleGuard],      // Applies to all children
        canActivate: [roleGuard],   // Applies to all children
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin]}
      },
      {
       path: 'landing-codes', loadComponent: () => import("./feature/on-board/landing-codes.component").then(m => m.LandingCodesComponent) ,
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin]}
      },
      {
        path: 'admin',
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin]},
        children: [
          {
            path: 'team',
            loadComponent: () => import("./feature/admin-team/admin-team.component").then(m => m.AdminTeamComponent),
            canActivate: [roleGuard],
            // Every sub-admin endpoint requires admin.superadmin, so gate the page on it too.
            data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permission: SUPER_ADMIN_PERMISSION},
          },
          // Carries its own guard and permission rather than putting them on the `admin` parent,
          // which would also gate `team` behind the invitations permission.
          {
            path: 'invitations',
            loadComponent: () => import('./feature/admin-invitations/admin-invitations.component')
              .then(m => m.AdminInvitationsComponent),
            canActivate: [roleGuard],
            data: {...ADMIN_INVITATIONS_ACCESS}
          },
          // The audit views. Same access constant as the list above: both endpoints are gated on
          // invitations.view, and neither has a write to gate on invitations.manage.
          {
            path: 'invitations/audit',
            loadComponent: () => import('./feature/admin-invitations/admin-invitation-audit.component')
              .then(m => m.AdminInvitationAuditComponent),
            canActivate: [roleGuard],
            data: {...ADMIN_INVITATIONS_ACCESS}
          }
        ]
      },
      // Matched before `project` below so an AI admin reaches it without the project group;
      // the URL stays where existing links expect it.
      {
        path: 'project/ai-models', loadComponent: () => import("./feature/ai-model-config/ai-model-config.component").then(m => m.AiModelConfigComponent),
        canMatch: [roleGuard],
        canActivate: [roleGuard],
        data: {...AI_MODELS_ACCESS}
      },
      {
        path: 'project',
        canMatch: [roleGuard],      // Applies to all children
        canActivate: [roleGuard],   // Applies to all children
        data: {roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permissionGroup: 'project'},
        children:[
          {path: 'settings', loadComponent: () => import("./feature/commons/settings/settings.component").then(m => m.SettingsComponent)},
          {path: 'cities', loadComponent: () => import("./feature/commons/cities/cities.component").then(m => m.CitiesComponent)},
          {path: 'cuisines', loadComponent: () => import("./feature/commons/cuisines/cuisines.component").then(m => m.CuisinesComponent)},
          {path: 'common-supplies', loadComponent: () => import("./feature/commons/common-supplies/common-supplies.component").then(m => m.CommonSuppliesComponent)},
          {path: 'supplies-specialization', loadComponent: () => import("./feature/commons/supplies-specialization/supplies-specialization.component").then(m => m.SuppliesSpecializationsComponent)},
          {path: 'supplier-types', loadComponent: () => import("./feature/commons/supplier-types/supplier-types.component").then(m => m.SupplierTypesComponent)},
          {path: 'predefined-certificates', loadComponent: () => import("./feature/predefined-certificates/predefined-certificates.component").then(m => m.PredefinedCertificatesComponent)},
          {path: 'certificate-categories', loadComponent: () => import("./feature/certificate-categories/certificate-categories.component").then(m => m.CertificateCategoriesComponent)},
          {path: 'deficit-reasons', loadComponent: () => import("./feature/grn/deficit-reasons/deficit-reasons.component").then(m => m.DeficitReasonsComponent)},
          {
            path: 'subscription-plans',
            children: [
              {path: '', pathMatch: 'full', redirectTo: 'supplier'},
              {path: 'supplier', loadComponent: () => import("./feature/subscription-plans/subscription-plans.component").then(m => m.SubscriptionPlansComponent), data: {audience: FeatureAudience.Supplier}},
              {
                path: 'supplier/plan/:id',
                data: {audience: FeatureAudience.Supplier},
                loadComponent: () => import('./feature/subscription-plans/plans/plan-editor.component').then(m => m.PlanEditorComponent),
              },
              {path: 'buyer', loadComponent: () => import("./feature/subscription-plans/subscription-plans.component").then(m => m.SubscriptionPlansComponent), data: {audience: FeatureAudience.Buyer}},
              {
                path: 'buyer/plan/:id',
                data: {audience: FeatureAudience.Buyer},
                loadComponent: () => import('./feature/subscription-plans/plans/plan-editor.component').then(m => m.PlanEditorComponent),
              },
            ],
          },
      ]
      },

      // {path: '', re  pathMatch: 'full' }, // Default to 'users'

    ]

  },

  // {path: 'settings', loadComponent: () => import("./feature/orders/orders.component").then(m => m.OrdersComponent)
  //   ,canMatch: [roleGuard],      // Applies to all children
  //   canActivate: [roleGuard],   // Applies to all children
  //   data: { roles: [RoleEnum.Admin] }}, // Base role requirement,},

  {path: 'login', component: LoginComponent},

  {path: 'access-denied', loadComponent: () => import("./layout/access-denied/access-denied.component").then(m => m.AccessDeniedComponent)},
  {path: 'no-permissions', loadComponent: () => import("./layout/no-permissions/no-permissions.component").then(m => m.NoPermissionsComponent)},

  {path: '**', component: AppLayoutComponent},

];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
